// The lanes that are not outcome groups: two totals, plus the handled lane that holds the agent list.
export const TASK_FLOW_LANE_INCOMING = 'incoming';
export const TASK_FLOW_LANE_IN_QUEUE = 'inQueue';
export const TASK_FLOW_LANE_HANDLED = 'handled';

/**
 * Outcomes the Collector can report, mirroring `TceOutcome`. Values are lower-cased because that is
 * how the Collector keys its aggregates; the member names carry the casing the UI reads back.
 */
export enum TaskFlowOutcome {
  CustomerHangupBeforeAccept = 'customerhangupbeforeaccept',
  CustomerHangupInIvr = 'customerhangupinivr',
  CustomerHangupInIvrAfterQueue = 'customerhangupinivrafterqueue',
  CustomerHangupInQueue = 'customerhangupinqueue',
  DestinationAccepted = 'destinationaccepted',
  DestinationDeclined = 'destinationdeclined',
  DestinationNotReached = 'destinationnotreached',
  ExternalSystemCancelled = 'externalsystemcancelled',
  ExternalSystemCancelledAfterQueue = 'externalsystemcancelledafterqueue',
  ExternalSystemCancelledBeforeAccept = 'externalsystemcancelledbeforeaccept',
  ExternalSystemCancelledInQueue = 'externalsystemcancelledinqueue',
  Failed = 'failed',
  ForwardedByWorkflow = 'forwardedbyworkflow',
  LostInQueue = 'lostinqueue',
  RemovedByRigorousWatchdog = 'removedbyrigorouswatchdog',
  RemovedManually = 'removedmanually',
  StandbyDutyExternalAccepted = 'standbydutyexternalaccepted',
  UserAborted = 'useraborted',
  UserAccepted = 'useraccepted',
  UserConferenced = 'userconferenced',
  UserExternalTransferFailed = 'userexternaltransferfailed',
  UserExternalTransferSuccessful = 'userexternaltransfersuccessful',
  UserInternalTransferFailed = 'userinternaltransferfailed',
  UserInternalTransferSuccessful = 'userinternaltransfersuccessful',
  UserMarkedAsHandled = 'usermarkedashandled',
  WorkflowCallbackScheduleSuccessful = 'workflowcallbackschedulesuccessful',
  WorkflowConversationRecorded = 'workflowconversationrecorded',
  WorkflowDisconnect = 'workflowdisconnect',
  WorkflowDisconnectAfterQueue = 'workflowdisconnectafterqueue',
  WorkflowExternalTransferFailed = 'workflowexternaltransferfailed',
  WorkflowExternalTransferSuccessful = 'workflowexternaltransfersuccessful',
  WorkflowInternalTransferFailed = 'workflowinternaltransferfailed',
  WorkflowInternalTransferSuccessful = 'workflowinternaltransfersuccessful',
  WorkflowMarkedAsHandled = 'workflowmarkedashandled',
}

/**
 * Closed palette, so a configured canvas still reads as part of the product. The values are stored
 * in the browser, so members are only ever added.
 */
export enum TaskFlowBucketColor {
  Red = 'red',
  Copper = 'copper',
  Orange = 'orange',
  Berry = 'berry',
  Blue = 'blue',
  Teal = 'teal',
  Gray = 'gray',
  Green = 'green',
  Purple = 'purple',
  Jungle = 'jungle',
  Olive = 'olive',
  Gunmetal = 'gunmetal',
}

/** Why a read failed, so the page can say something more useful than "unavailable". */
export enum TaskFlowLoadFailure {
  None = 'none',
  /** 503 — the backend switch is off, so there is nothing to read yet. */
  Disabled = 'disabled',
  /** 403 — the tenant does not have the feature flag on. */
  Forbidden = 'forbidden',
  /** Anything else: transport, timeout, 5xx. */
  Unavailable = 'unavailable',
}

/** What is driving the canvas. */
export enum TaskFlowMode {
  /** Aggregates for the current window, refreshed by SignalR deltas. */
  Live = 'live',
  /** A past window read back from the event stream and animated in event-time order. */
  Replay = 'replay',
}

/**
 * What an agent row stands for. The two sentinels belong to no user, so the UI supplies their label and
 * their icon; the values match the keys the Collector aggregates them under.
 */
/**
 * What a drop-off row's volume was collected against. Mirrors `TaskFlowTargetType` on the read model,
 * which the Collector serializes by name, so a row says what it is rather than being inferred from the
 * shape of its identifier. A value this build does not know reads as `Unknown` and is rendered as such.
 */
export enum TaskFlowAgentKind {
  /**
   * A member of this service handled the task. Membership is what makes an agent, not being a person: a
   * virtual user belonging to the service is an agent of it.
   */
  User = 'Agent',
  /** A person outside this service, reached by a transfer. Has presence, but never staffed the service. */
  ExternalUser = 'User',
  /** Another service, identified by its resource account. */
  Service = 'Service',
  /** An external number. */
  Pstn = 'Pstn',
  /** The workflow took a voice message, so the service dealt with the task itself. */
  Voicemail = 'Voicemail',
  /** The task reached nothing at all — abandoned in queue, or failed before a target was tried. */
  Unassigned = 'None',
  /**
   * The response stated no kind. Not a kind the backend sends — it has none for "no idea" — but what this
   * build sees when a newer one names a target it does not know, or when the window held nothing that
   * said. Rendered as unidentified rather than assumed to be a person.
   */
  Unstated = '',
}

/** What a transfer target is, as the read model derives it from the transfer type. */
export enum TaskFlowDestinationKind {
  User = 'user',
  Service = 'service',
  External = 'external',
  /** The volume below the listed destinations, as one row rather than a target of its own. */
  Other = 'other',
  Unknown = '',
}

/** Flow axis of the drop-off canvas. */
export enum TaskFlowOrientation {
  LeftToRight = 'leftright',
  TopDown = 'topdown',
}

/**
 * One configurable drop-off bucket. The Collector stores its aggregates per outcome, so a change here
 * re-groups the whole retention window rather than only the tasks completed afterwards.
 */
export interface ITaskFlowBucket {
  /** Stable key the lane counts are keyed by. Generated for a bucket the user adds. */
  id: string;
  /** Empty to keep the built-in translated label; a non-empty value overrides it. */
  name: string;
  color: TaskFlowBucketColor;
  /** Counted against the reliability meter — a technical failure rather than a caller giving up. */
  isFailure: boolean;
  /** Outcome names, lower-cased. */
  outcomes: string[];
  hidden: boolean;
  /** The handled lane: renameable and recolourable, but never removed or hidden. */
  fixed?: boolean;
}

/**
 * The bucket configuration as it is stored in the browser. The outcomes known when it was written are
 * kept beside it, so a later release can tell an outcome it has just added from one the reader chose
 * to leave unassigned.
 */
export interface ITaskFlowBucketSettings {
  buckets: ITaskFlowBucket[];
  knownOutcomes: string[];
}

export interface ITaskFlowLane {
  id: string;
  labelKey?: string;
  /** Configured bucket name; overrides labelKey. */
  label?: string;
  hintKey?: string;
  /** Outcomes this lane collects; overrides hintKey. */
  hint?: string;
  color: TaskFlowBucketColor;
  /** False for the two totals, which cannot be hidden or reordered. */
  filterable: boolean;
  isAgentLane: boolean;
  /** Drawn faded and excluded from dot traffic, but still laid out so the funnel keeps its shape. */
  dimmed: boolean;
}

/** A lane as rendered: lane descriptor plus its measured counters. */
export interface ITaskFlowLaneViewModel extends ITaskFlowLane {
  count: number;
  /** Share of the incoming volume, 0-100. */
  percentage: number;
}

export interface ITaskFlowOutcomeCount {
  outcome: string;
  count: number;
}

export interface ITaskFlowAgent {
  id: string;
  /**
   * AAD object id of the agent, for resolving duty state and the Graph avatar photo. Despite the name,
   * not Nimbus's own internal user id - the Collector's `AgentUserId` is already the O365 id. Empty for
   * a sentinel row and for anonymized tasks.
   */
  userId: string;
  displayName: string;
  kind: TaskFlowAgentKind;
  handledCount: number;
  /**
   * How much of {@link handledCount} the agent placed rather than answered. Kept beside the combined
   * total rather than replacing it, so ranking, the leader ring and every share stay measured against
   * the whole of what an agent handled.
   */
  outboundHandledCount: number;
  /**
   * How much of {@link handledCount} the agent answered. Counted in its own right rather than taken as
   * the remainder: a task type can state no direction, so this and {@link outboundHandledCount} need
   * not add up to the total, and what is left over is the direction-less volume.
   */
  inboundHandledCount: number;
  /** Mean queue time of this agent's tasks in the window. */
  averageQueueMs: number;
  /** Share of the service's handled volume, 0-100. */
  handledShare: number;
  /** Highlighted with the green outer ring — the top handler of the window. */
  isLeader: boolean;
  outcomeBreakdown: ITaskFlowOutcomeCount[];
  /** Team id of the service a {@link TaskFlowAgentKind.Service} row is, once its resource account is matched. */
  targetTeamId?: string;
}

export enum TaskFlowAgentNavigationTarget {
  Voicemails = 'voicemails',
  Service = 'service',
}

/** Where a row leads: the selected service's voicemails, or another service's own Task Flow view. */
export interface ITaskFlowAgentNavigation {
  target: TaskFlowAgentNavigationTarget;
  teamId?: string;
  teamName?: string;
}

/**
 * The Direct Call Handling proxy's one owning user, when the selected service is one — a per-task
 * `agent.userId`/`displayName` cannot be trusted to resolve to the real person there, since a direct
 * call never carries the "who accepted from the queue" event the read model would otherwise read
 * them off. A DCH service only ever has this one user, so this is used in their place.
 */
export interface ITaskFlowDirectCallOwningUser {
  id: string;
  /** Resolved a moment after {@link id}, off team membership rather than the team info read. */
  displayName: string | null;
}

export interface ITaskFlowServiceVisibility {
  /** Services the selection must not offer: reporting permissions missing, or "Hide User Statistics from Reporting" set. */
  hiddenServiceIds: Set<string>;
  /** The selected service once its setting has been confirmed off; null while it is hidden or still being checked. */
  readableServiceId: string | null;
}

/**
 * Where the volume that left the service went. The target-less row in the agent list holds that volume as
 * one number; this is the breakdown behind it.
 */
export interface ITaskFlowTransferDestination {
  /** The transfer that reached the target, naming the initiator too — `UserToService`, `ServiceToExternal`. */
  transferType: string;
  kind: TaskFlowDestinationKind;
  /** The target in clear: a UPN, a service resource account, or a number. */
  destination: string;
  /** Resolved name for a user target; the target itself for a service or a number. */
  displayName: string;
  /** Nimbus user id when the target is a user, so its duty state can be resolved. Empty otherwise. */
  userId: string;
  /** Service id when the target is a service, so the caller can name it. Empty otherwise. */
  serviceId: string;
  count: number;
  /** Share of the transferred-out volume, 0-100. */
  share: number;
}

/** Drop-off snapshot for a single service and time window. */
export interface ITaskFlowDropoff {
  serviceId: string;
  from: Date;
  to: Date;
  windowMinutes: number;
  /** Tasks that entered the service in the window. */
  incoming: number;
  /**
   * The outbound part of {@link incoming}, counted over exactly the same population rather than beside
   * it, so the two must not be added. Subtracting it does not leave inbound either: a task type can
   * carry no direction at all and still counts towards the total. Zero while the read model states no
   * direction, and for a replayed window.
   */
  outgoing: number;
  /** Tasks currently waiting in the queue. */
  inQueue: number;
  /** Number of currently active queue positions. */
  position: number;
  handled: number;
  avgQueueForHandledMs: number;
  /** Kept so a grouping change can re-group the window already on screen without another read. */
  outcomeCounts: Record<string, number>;
  laneCounts: Record<string, number>;
  topAgents: ITaskFlowAgent[];
  /** Busiest first. Empty until a task in the window was transferred out of the service. */
  transferDestinations: ITaskFlowTransferDestination[];
}

/** Aggregates shown in the KPI row above the canvas. */
export interface ITaskFlowKpis {
  totalTasks: number;
  queueMs: number;
  avgQueueMs: number;
  inQueue: number;
  dropOff: number;
  /** Drop-off tasks as a share of incoming, 0-100. */
  dropOffShare: number;
  handled: number;
  /** Handled tasks as a share of incoming, 0-100. */
  handledShare: number;
  /** Non-failed tasks as a share of all tasks — the backend `task_SLA` meter. */
  taskSlaPercent: number;
  /** Offered tasks accepted by an agent, as a percentage. */
  acceptanceSlaPercent: number;
  /** Handled tasks that met the SLA, as a percentage. */
  underSlaPercent: number;
}

/** A range option of the drilldown's handled histogram. */
export interface ITaskFlowHistogramRange {
  id: string;
  labelKey: string;
  /** Length of the range, in minutes. */
  minutes: number;
  /** Number of bars drawn; the API's per-bucket points are aggregated into these. */
  bars: number;
  /** Label for the left edge of the axis. */
  spanLabelKey: string;
}

/** The handled histogram of one agent, aggregated into display bars. */
export interface ITaskFlowHistogram {
  agentId: string;
  rangeId: string;
  values: number[];
  peak: number;
}

/** One day of the task overview. */
export interface ITaskFlowOverviewDay {
  /** Local calendar date, `yyyy-mm-dd`, so a bar can be identified across reloads. */
  key: string;
  /** Local midnight the day starts at — also the start of the range a replay of it reads. */
  date: Date;
  tasks: number;
  handled: number;
}

/** Task volume per day over the selected range, as the overview plots it. */
export interface ITaskFlowOverview {
  serviceId: string;
  rangeDays: number;
  days: ITaskFlowOverviewDay[];
  /** Busiest day in the range; bars are scaled against it so a quiet range still reads. */
  peak: number;
  total: number;
  /** False when the source reports task volume only, so the tooltip leaves handled out. */
  hasHandled: boolean;
}

/**
 * One completed task, as replayed onto the canvas.
 *
 * Both ends are held rather than one point in time, because the canvas animates the task over the
 * span it actually occupied. The two sources report opposite ends of it — the Collector stream is
 * written when a task terminates, reporting stores the moment it started — so each mapper works out
 * the other end from the duration and hands over the same pair.
 */
/**
 * Which way a task ran. `None` is a real answer rather than a missing one — `ExternalTask` and
 * `MSTeamsDirectUnknown` state no direction — so it is never counted as either of the other two.
 */
export enum TaskFlowDirection {
  Inbound = 'Inbound',
  Outbound = 'Outbound',
  None = 'None',
}

export interface ITaskFlowEvent {
  startedAt: Date;
  endedAt: Date;
  laneId: string;
  agentId: string;
  agentDisplayName: string;
  /** O365 id of the agent, when the stream entry carried one — empty for a sentinel or unresolved row. */
  agentUserId: string;
  queueMs: number;
  durationMs: number;
  direction: TaskFlowDirection;
  /**
   * The raw outcome the task ended on, kept beside {@link laneId} rather than replaced by it: the lane
   * is a grouping of outcomes, but an agent's outcome mix is counted per outcome, not per lane.
   */
  outcome?: string;
}

/** A dot reaching its lane: what the canvas reports so the counters can move with the animation. */
export interface ITaskFlowLanding {
  laneId: string;
  /** Empty unless the task landed on an agent row. */
  agentId: string;
  /** What the task waited before being handled. */
  queueMs: number;
  /** Carried so the tallies can move by direction as the dot lands. */
  direction: TaskFlowDirection;
  /** Carried so the agent's outcome mix can move as the dot lands. */
  outcome?: string;
}

/** Running total for one agent while a window replays. */
export interface ITaskFlowAgentTally {
  displayName: string;
  /** O365 id of the agent, so a replayed row can resolve a Graph photo the same way a live one does. */
  userId: string;
  handledCount: number;
  /** The part of {@link handledCount} replayed so far that the agent placed. */
  outboundHandledCount: number;
  /** The part of {@link handledCount} replayed so far that the agent answered. */
  inboundHandledCount: number;
  /** Mean queue time of the tasks replayed onto this agent so far. */
  averageQueueMs: number;
  /** Place in the whole window's ranking, so a row keeps it while the counts climb. */
  rank: number;
  /**
   * Raw per-outcome counts replayed onto this agent so far, across every lane — a decline counts here
   * exactly as an accept does. Turned into {@link ITaskFlowAgent.outcomeBreakdown} once the window renders.
   */
  outcomeCounts?: Record<string, number>;
}

/** A replayable window: its events plus where the cursor stopped. */
export interface ITaskFlowReplay {
  serviceId: string;
  from: Date;
  to: Date;
  events: ITaskFlowEvent[];
  /** True when the page cap stopped the read before the window was exhausted. */
  isTruncated: boolean;
  /**
   * True when the window came from Reporting rather than the LiveData cache — either the cache is
   * unreachable, or `from` sits outside its retention (a picked day is not bounded by that retention,
   * which is far shorter than the history Reporting still offers for the same day).
   */
  isFromReporting: boolean;
}

/**
 * Task volume across the replayed day, one value per reporting slot, for the scrubber to draw.
 *
 * Read from reporting rather than counted off {@link ITaskFlowReplay.events}: the replayed window is
 * capped at a page, so on a busy day counting the loaded events would draw a shape that stops partway
 * through the day and read as though the service had gone quiet.
 */
export interface ITaskFlowReplaySlots {
  serviceId: string;
  /** Start of the first slot — the window's own start, so an index gives the slot its time. */
  from: Date;
  /** Length of one slot in milliseconds. Reporting keeps 15-minute slots. */
  slotMs: number;
  values: number[];
  /** Handled tasks per slot, aligned with {@link values}, for the hover readout. */
  handled: number[];
  /** Busiest slot in the window, so bars can be scaled against it without a second pass. */
  peak: number;
}

// -- API contract: LiveData read model, GET /api/v1/taskflows/{tenantId}/... --
// Mirrors the Task Flow API response models (camel-cased by the serializer).

export interface ITaskFlowTopAgentResponse {
  /**
   * What the row's volume is collected under: the agent's O365 id, the target a workflow handed the task
   * to, or a sentinel. Never a hash — it is also what identifies the row back to the API.
   */
  targetIdentifier?: string;
  agentUserId?: string;
  /** Which kind of thing `targetIdentifier` names, so each row is rendered as what it is. */
  targetType?: TaskFlowAgentKind;
  displayName?: string;
  handledCount?: number;
  /** The outbound part of `handledCount`. Absent from a build that does not aggregate direction yet. */
  outboundHandledCount?: number;
  /** The inbound part of `handledCount`. Absent from a build that counts outbound only. */
  inboundHandledCount?: number;
  averageQueueMilliseconds?: number;
  outcomeBreakdown?: Record<string, number>;
  isLeader?: boolean;
}

export interface ITaskFlowTransferDestinationResponse {
  transferType?: string;
  destinationKind?: string;
  destination?: string;
  displayName?: string;
  destinationUserId?: string;
  destinationServiceId?: string;
  count?: number;
}

export interface ITaskFlowDropoffResponse {
  tenantId?: string;
  serviceId?: string;
  from?: string;
  to?: string;
  windowMinutes?: number;
  incomingCount?: number;
  /** Outbound tasks placed in the window. Absent from a build that does not aggregate direction yet. */
  outgoingCount?: number;
  inQueueCount?: number;
  positionCount?: number;
  handledCount?: number;
  avgQueueForHandledMs?: number;
  /** Ungrouped; the reader decides which lane each outcome belongs to. */
  outcomeCounts?: Record<string, number>;
  topAgents?: ITaskFlowTopAgentResponse[];
  transferDestinations?: ITaskFlowTransferDestinationResponse[];
}

export interface ITaskFlowKpisResponse {
  totalEdgeCount?: number;
  totalQueueMilliseconds?: number;
  totalInboundCount?: number;
  totalHandledCount?: number;
  serviceNodeCount?: number;
  agentNodeCount?: number;
  underSlaPercent?: number;
  underSlaCount?: number;
}

export interface ITaskFlowSnapshotResponse {
  tenantId?: string;
  serviceId?: string;
  from?: string;
  to?: string;
  windowMinutes?: number;
  kpis?: ITaskFlowKpisResponse;
}

export interface ITaskFlowEventResponse {
  timestamp?: string;
  outcome?: string;
  targetIdentifier?: string;
  agentDisplayName?: string;
  agentUserId?: string;
  queueMilliseconds?: number;
  durationMilliseconds?: number;
  /** `Inbound`, `Outbound`, or absent for a task type that states no direction. */
  direction?: string;
}

export interface ITaskFlowEventsResponse {
  tenantId?: string;
  serviceId?: string;
  from?: string;
  to?: string;
  nextCursor?: string;
  events?: ITaskFlowEventResponse[];
}

export interface ITaskFlowDataPointResponse {
  timestamp?: string;
  value?: number;
}

export interface ITaskFlowMetricSeriesResponse {
  metric?: string;
  serviceId?: string;
  agentId?: string;
  points?: ITaskFlowDataPointResponse[];
}

export interface ITaskFlowTimeSeriesResponse {
  tenantId?: string;
  from?: string;
  to?: string;
  bucketMinutes?: number;
  series?: ITaskFlowMetricSeriesResponse[];
}

export interface ITaskFlowLiveAgentResponse {
  userId?: string;
  displayName?: string;
  inProgressCount?: number;
}

/** What the service has ringing, in an IVR, in queue and in progress right now; not additive with the finished-task counts. */
export interface ITaskFlowLiveResponse {
  tenantId?: string;
  serviceId?: string;
  asOf?: string;
  inQueueCount?: number;
  longestWaitSeconds?: number;
  ringingCount?: number;
  inIvrCount?: number;
  inProgressCount?: number;
  /** Every task in the service right now, each counted once; absent from a backend that predates it. */
  totalCount?: number;
  agentsHidden?: boolean;
  agents?: ITaskFlowLiveAgentResponse[];
}

/** The stacked areas of the NOW lane, top to bottom; values double as their CSS modifiers. */
export enum TaskFlowNowArea {
  Ringing = 'ringing',
  InIvr = 'inIvr',
  InQueue = 'inQueue',
  InProgress = 'inProgress',
}
